import { useState, useEffect } from "react";
import API_BASE_URL from "../config";
import { LoaderCircle, X } from "lucide-react";
import { MatchingResults, StatTiles, SummaryBar, MATCHING_TAB } from "./matching";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import ComplianceBrief from "./approval/ComplianceBrief";
import ApprovalTable from "./approval/ApprovalTable";
import "./LVWorkflow.css";
import "./approval/ApprovalAnalysis.css";

const ICON_SIZE = 14;

/**
 * Read-only view of a report snapshot. Used for two sources, sharing ONE renderer
 * so the live and read-only views stay identical (DRY):
 *  - Sidebar "Recent Runs": pass `runId` → fetches GET /api/v1/runs/{id}.
 *  - Sidebar "Background Jobs": pass a pre-built `run` object (no fetch) for a
 *    completed/failed background job.
 *
 * Compliance/discovery briefs render through the same <ComplianceBrief> the live
 * DiscoveryView uses; matching reports reuse the matching review tables; approval
 * reports reuse <ApprovalTable> exactly as ApprovalAnalysis renders them.
 */
export default function SavedRunView({ runId, run: runProp, onClose, onRetry }) {
  const [run, setRun] = useState(runProp || null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(MATCHING_TAB.SUPPLY);
  const [documentUrl, setDocumentUrl] = useState("");
  const [zAssets, setZAssets] = useState({}); // approval: z-document assets, lazily fetched

  // When given a pre-built run object (background job), use it directly.
  useEffect(() => {
    if (!runProp) return undefined;
    setRun(runProp);
    setError(null);
    setActiveTab(MATCHING_TAB.SUPPLY);
    setDocumentUrl("");
    return undefined;
  }, [runProp]);

  useEffect(() => {
    if (!runId) return undefined; // pre-built run path: nothing to fetch
    let cancelled = false;
    setRun(null);
    setError(null);
    setActiveTab(MATCHING_TAB.SUPPLY);
    setDocumentUrl("");
    fetch(`${API_BASE_URL}/api/v1/runs/${runId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Could not load run (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setRun(data);
        // Best-effort: resolve the source document URL so the "Quelle" column
        // can deep-link into the PDF. A failure here just leaves sources as text.
        if (data?.lv_document_id) {
          fetch(`${API_BASE_URL}/api/v1/documents/status/${data.lv_document_id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((doc) => { if (!cancelled && doc?.document_url) setDocumentUrl(doc.document_url); })
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  // Lazily fetch Z-document assets for the approval table (shared with ApprovalAnalysis).
  const fetchZAssets = (docId, database) => {
    if (!docId) return;
    let started = false;
    setZAssets((prev) => {
      if (prev[docId]) return prev;
      started = true;
      return { ...prev, [docId]: "loading" };
    });
    if (!started) return;
    fetch(`${API_BASE_URL}/api/v1/approval/z-document-assets`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_id: docId, database: database || "Betonbau" }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => setZAssets((prev) => ({ ...prev, [docId]: data })))
      .catch(() => setZAssets((prev) => ({ ...prev, [docId]: "error" })));
  };

  const closeButton = (
    <button type="button" className="ds-btn ds-btn--secondary" onClick={onClose}>
      <X size={ICON_SIZE} aria-hidden="true" />
      Close
    </button>
  );

  if (error) {
    return (
      <div className="lvwf-root">
        <div className="ds-alert ds-alert--danger ds-flush" role="alert">{error}</div>
        <div className="lvwf-actions">{closeButton}</div>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="lvwf-root">
        <p className="ds-empty lvwf-loading" role="status">
          <LoaderCircle size={ICON_SIZE} className="ds-spin" aria-hidden="true" />
          Loading saved run…
        </p>
      </div>
    );
  }

  // A failed background job has no report — show the error and a Retry control.
  if (run.status === "failed") {
    return (
      <div className="lvwf-root">
        <RunHeader
          title={`${run.workflow_id || run.agent_type} · ${run.project_name}`}
          status="failed"
          statusText="Fehlgeschlagen"
          actions={(
            <>
              {onRetry && (
                <button type="button" className="ds-btn ds-btn--primary" onClick={onRetry}>
                  ↻ Erneut versuchen
                </button>
              )}
              {closeButton}
            </>
          )}
        />
        <div className="ds-alert ds-alert--danger ds-flush" role="alert">{run.error || "Job failed."}</div>
      </div>
    );
  }

  const report = run.report || {};
  const summary = report.summary || {};
  const createdAt = run.created_at
    ? new Date(run.created_at).toLocaleString("de-DE")
    : "";
  // Compliance/discovery briefs carry `sections` with column-keyed rows; matching
  // reports carry can_supply/missing. Render whichever this run is.
  const isCompliance = run.agent_type === "compliance"
    || (Array.isArray(report.sections) && report.sections.some((s) => Array.isArray(s.rows)));
  // Approval reports carry `results` (per-position Z-conditions) + summary.within_scope.
  const isApproval = run.agent_type === "approval"
    || (Array.isArray(report.results) && summary.within_scope != null);

  const head = (
    <RunHeader
      title={`${run.workflow_id} · ${run.project_name}`}
      status={run.verdict ? "approved" : "rejected"}
      subtitle={`Approved run${createdAt ? ` · ${createdAt}` : ""} · read-only`}
      actions={closeButton}
    />
  );

  if (isCompliance) {
    return (
      <div className="lvwf-root">
        {head}
        <ComplianceBrief
          brief={{ document_name: run.project_name, ...report }}
          documentUrl={documentUrl}
          documentId={run.lv_document_id || ""}
          database={run.database || ""}
        />
      </div>
    );
  }

  if (isApproval) {
    return (
      <div className="lvwf-root">
        {head}
        <StatTiles
          stats={[
            { label: "Total positions", value: summary.total },
            { label: "Within scope", value: summary.within_scope },
            { label: "No Z-doc", value: summary.no_z_doc },
          ]}
        />
        <ApprovalTable
          results={report.results}
          assets={zAssets}
          fetchAssets={(docId) => fetchZAssets(docId, run.database)}
          database={run.database || ""}
        />
      </div>
    );
  }

  return (
    <div className="lvwf-root">
      {head}

      {summary.total != null && <SummaryBar summary={summary} />}

      <MatchingResults
        report={report}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        documentUrl={documentUrl}
      />
    </div>
  );
}

// Page header of a saved run: title + status badge, subtitle, and the actions
// (Close / Retry) aligned to the right so they are reachable without scrolling.
function RunHeader({ title, status, statusText, subtitle, actions }) {
  return (
    <div className="ds-page-header lvwf-run-header">
      <div className="lvwf-run-heading">
        <div className="lvwf-run-title">
          <h1 className="ds-page-title ds-flush">{title}</h1>
          <span className={statusBadgeClass(status)}>{statusText || statusLabel(status)}</span>
        </div>
        {subtitle && <p className="ds-page-subtitle">{subtitle}</p>}
      </div>
      <div className="lvwf-actions">{actions}</div>
    </div>
  );
}
